import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, take, tap } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { SortOrder } from '../core/sort';
import { User } from '../core/user';

declare interface UsersResponse {
  totalResults: number;
  loadMoreUrl: string;
  users: User[];
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private sortOn = 'first_name';
  private sortDirection = 'ASCENDING';
  private searchUrl = '/_ajax/v1/manage-users/search/';
  private isInitialState = true;

  private loadMoreUrl$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private loadingUsersSubscription: Subscription;

  public totalResults = 0;

  private users$$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private user$$: BehaviorSubject<User> = new BehaviorSubject<User>(null);

  get isLoading$(): Observable<boolean> {
    return this.isLoading$$.asObservable();
  }

  get hasMoreResults(): Observable<boolean> {
    return this.loadMoreUrl$$.asObservable().pipe(map((x) => !!x));
  }

  constructor(
    private apiService: ApiService,
    private snackbarService: SnackbarService,
    private router: Router,
    private translate: TranslateService,
  ) {}

  get user$(): Observable<User> {
    return this.user$$.asObservable().pipe(shareReplay(1));
  }

  get users$(): Observable<User[]> {
    return this.users$$.asObservable().pipe(shareReplay(1));
  }

  public search(terms: Observable<string>, debounceMs = 300): Observable<User[]> {
    terms.pipe(debounceTime(debounceMs), distinctUntilChanged()).subscribe((term) => {
      this.isInitialState = true;
      this.loadUsers(this.searchUrl, term, false);
    });
    return this.users$$.asObservable();
  }

  public setSort(orderBy: string, order: SortOrder): void {
    this.sortOn = orderBy;
    this.sortDirection = order === SortOrder.Ascending ? 'ASCENDING' : 'DESCENDING';
    this.isInitialState = true;
    this.loadUsers(this.searchUrl, null, false);
  }

  public loadMore(): void {
    if (this.loadMoreUrl$$.getValue()) {
      this.loadUsers(this.loadMoreUrl$$.getValue(), null, true);
    }
  }

  public delete(user: User): void {
    const url = `/_ajax/v1/delete-user/`;
    const body = { vbc_user_id: user.userId };
    this.apiService
      .post(url, body)
      .pipe(
        map(() => true),
        catchError(() => {
          this.snackbarService.errorSnack(this.translate.instant('USER_PERMISSIONS.DELETE_USER_FAILED'));
          return of(null);
        }),
        take(1),
      )
      .subscribe((success) => {
        if (success) {
          this.router.navigateByUrl('/bc-admin');
          const currentUsers = this.users$$.getValue();
          this.users$$.next(currentUsers.filter((next) => next.userId !== user.userId));
          this.snackbarService.successSnack(this.translate.instant('USER_PERMISSIONS.DELETE_USER_SUCCESS'));
        }
      });
  }

  public toggleNotificationsEnabled(user: User, value: boolean): Observable<boolean> {
    const url = `/_ajax/v1/manage-users/toggle-notifications-enabled/`;
    const body = { userId: user.userId, enableFlag: value };
    return this.apiService.post(url, body).pipe(map(() => true));
  }

  private processUsersReponse(result: any): UsersResponse {
    const totalResultsKey = 'total_results';
    const usersKey = 'users';
    const loadMoreUrlKey = 'next_url';

    return {
      totalResults: result[totalResultsKey],
      users: result[usersKey].map((userData) => new User(userData)),
      loadMoreUrl: result[loadMoreUrlKey],
    };
  }

  private loadUsers(url: string, query?: string, append = false): void {
    const body = { filterQuery: query, sort: this.sortOn, direction: this.sortDirection };
    const loadInitialUsers = this.apiService.post(url, body);
    const loadMoreUsers = this.apiService.get(url, {
      params: {
        query: query,
      },
    });
    this.isLoading$$.next(true);
    if (this.loadingUsersSubscription) {
      this.loadingUsersSubscription.unsubscribe();
    }
    this.loadingUsersSubscription = (this.isInitialState ? loadInitialUsers : loadMoreUsers)
      .pipe(map(this.processUsersReponse))
      .subscribe((usersResponse) => {
        if (usersResponse.loadMoreUrl && usersResponse.loadMoreUrl !== this.loadMoreUrl$$.getValue()) {
          const fullUrl = '/_ajax/vbc/manage-users/more/?nextUrl=' + encodeURIComponent(usersResponse.loadMoreUrl);
          this.loadMoreUrl$$.next(fullUrl);
        } else {
          this.loadMoreUrl$$.next(null);
        }
        this.totalResults = usersResponse.totalResults;
        if (append) {
          this.users$$.next(this.users$$.getValue().concat(usersResponse.users));
        } else {
          this.users$$.next(usersResponse.users);
        }
        this.isLoading$$.next(false);
        this.isInitialState = false;
      });
  }

  // This will not cache the user like loadUser$ does. The purpose is to allow a user to be retrieved with a field mask that
  // does not interfear with the cached user.
  getUser$(userId: string, fieldMask?: Array<string>): Observable<User> {
    let url = `/_ajax/v1/manage-users/get/?userId=${userId}`;
    if (fieldMask) {
      fieldMask.forEach((m) => (url = url + `&fieldMask=${m}`));
    }
    return this.apiService.get(url).pipe(map((result) => new User(result)));
  }

  loadUser$(userId: string, fieldMask?: Array<string>): Observable<User> {
    this.user$$.next(null);
    return this.getUser$(userId, fieldMask).pipe(tap((user) => this.user$$.next(user)));
  }

  clearUserSubjects(): void {
    this.users$$.next(null);
  }
}
